/**
 * Validadores de resposta para cada tipo de entidade.
 * Cada validador recebe o response data e retorna { valid, errors[] }
 * onde errors lista as propriedades faltantes ou inválidas usadas no Admin.
 */

const isString = (v) => typeof v === 'string';
const isNumber = (v) => typeof v === 'number';
const isBool = (v) => typeof v === 'boolean';
const isArray = (v) => Array.isArray(v);
const isGuid = (v) => isString(v) && /^[0-9a-f-]{36}$/i.test(v);
const isOptionalString = (v) => v === null || v === undefined || isString(v);
const isOptionalNumber = (v) => v === null || v === undefined || isNumber(v);
const isDateString = (v) => isString(v) && !isNaN(Date.parse(v));
const isOptionalDate = (v) => v === null || v === undefined || isDateString(v);

function validate(data, rules, prefix = '') {
  const errors = [];
  for (const [field, check] of Object.entries(rules)) {
    const value = data?.[field];
    const fullField = prefix ? `${prefix}.${field}` : field;
    if (typeof check === 'function') {
      if (!check(value)) {
        errors.push(`${fullField}: esperado válido, recebeu ${JSON.stringify(value)} (${typeof value})`);
      }
    } else if (typeof check === 'object' && !Array.isArray(check)) {
      if (value && typeof value === 'object') {
        errors.push(...validate(value, check, fullField));
      }
    }
  }
  return errors;
}

function validateArray(data, itemRules, prefix = '') {
  const errors = [];
  if (!isArray(data)) {
    errors.push(`${prefix}: esperado array, recebeu ${typeof data}`);
    return errors;
  }
  data.forEach((item, i) => {
    errors.push(...validate(item, itemRules, `${prefix}[${i}]`));
  });
  return errors;
}

// --- Validadores por entidade ---

export function validateProfessional(data) {
  const errors = validate(data, {
    id: isGuid,
    name: isString,
    email: isOptionalString,
    phone: isOptionalString,
    specialty: isOptionalString,
    registrationNumber: isOptionalString,
  });
  return { valid: errors.length === 0, errors };
}

export function validateProfessionalsList(data) {
  if (!isArray(data)) return { valid: false, errors: ['Esperado array de profissionais'] };
  const errors = [];
  data.forEach((p, i) => {
    errors.push(...validate(p, {
      id: isGuid,
      name: isString,
    }, `[${i}]`));
  });
  return { valid: errors.length === 0, errors };
}

export function validateService(data) {
  const errors = validate(data, {
    id: isGuid,
    name: isString,
    description: isOptionalString,
    category: isOptionalString,
    defaultDurationMinutes: isNumber,
    defaultPrice: isNumber,
  });
  return { valid: errors.length === 0, errors };
}

export function validateServicesList(data) {
  if (!isArray(data)) return { valid: false, errors: ['Esperado array de serviços'] };
  const errors = [];
  data.forEach((s, i) => {
    errors.push(...validate(s, {
      id: isGuid,
      name: isString,
      defaultDurationMinutes: isNumber,
      defaultPrice: isNumber,
    }, `[${i}]`));
  });
  return { valid: errors.length === 0, errors };
}

export function validatePlace(data) {
  const errors = validate(data, {
    id: isGuid,
    name: isString,
    description: isOptionalString,
    phone: isOptionalString,
    email: isOptionalString,
  });
  return { valid: errors.length === 0, errors };
}

export function validatePlacesNormalized(data) {
  const errors = [];
  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Esperado objeto normalizado { professionals, services, payments, places }'] };
  }
  if (!isArray(data.professionals)) errors.push('professionals: esperado array');
  if (!isArray(data.services)) errors.push('services: esperado array');
  if (!isArray(data.payments)) errors.push('payments: esperado array');
  if (!isArray(data.places)) errors.push('places: esperado array');
  return { valid: errors.length === 0, errors };
}

export function validatePayment(data) {
  const errors = validate(data, {
    id: isGuid,
    name: isString,
    type: (v) => isNumber(v) || isString(v),
  });
  return { valid: errors.length === 0, errors };
}

export function validatePaymentsList(data) {
  if (!isArray(data)) return { valid: false, errors: ['Esperado array de pagamentos'] };
  const errors = [];
  data.forEach((p, i) => {
    errors.push(...validate(p, {
      id: isGuid,
      name: isString,
      type: (v) => isNumber(v) || isString(v),
    }, `[${i}]`));
  });
  return { valid: errors.length === 0, errors };
}

export function validatePlaceProfessional(data) {
  const errors = validate(data, {
    id: isGuid,
    professionalId: isGuid,
  });
  return { valid: errors.length === 0, errors };
}

export function validatePlaceService(data) {
  const errors = validate(data, {
    id: isGuid,
    serviceId: isGuid,
  });
  return { valid: errors.length === 0, errors };
}

export function validatePlaceServiceProfessional(data) {
  const errors = validate(data, {
    id: isGuid,
  });
  return { valid: errors.length === 0, errors };
}

export function validateSchedule(data) {
  const errors = validate(data, {
    id: isGuid,
    name: isString,
    placeProfessionalId: isGuid,
  });
  return { valid: errors.length === 0, errors };
}

export function validateSchedulesList(data) {
  if (!isArray(data)) return { valid: false, errors: ['Esperado array de schedules'] };
  const errors = [];
  data.forEach((s, i) => {
    errors.push(...validate(s, {
      id: isGuid,
      name: isString,
    }, `[${i}]`));
  });
  return { valid: errors.length === 0, errors };
}

export function validateShift(data) {
  const errors = validate(data, {
    id: isGuid,
    startTime: isString,
    endTime: isString,
  });
  return { valid: errors.length === 0, errors };
}

export function validateShiftsList(data) {
  if (!isArray(data)) return { valid: false, errors: ['Esperado array de shifts'] };
  const errors = [];
  data.forEach((s, i) => {
    errors.push(...validate(s, {
      id: isGuid,
      startTime: isString,
      endTime: isString,
    }, `[${i}]`));
  });
  return { valid: errors.length === 0, errors };
}

export function validatePerson(data) {
  const errors = validate(data, {
    id: isGuid,
    name: isString,
    email: isOptionalString,
    phone: isOptionalString,
  });
  return { valid: errors.length === 0, errors };
}

export function validatePersonsList(data) {
  if (!isArray(data)) return { valid: false, errors: ['Esperado array de persons'] };
  const errors = [];
  data.forEach((p, i) => {
    errors.push(...validate(p, {
      id: isGuid,
      name: isString,
    }, `[${i}]`));
  });
  return { valid: errors.length === 0, errors };
}

export function validateDashboardStats(data) {
  const errors = [];
  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Esperado objeto de stats'] };
  }
  return { valid: errors.length === 0, errors };
}

export function validateEntities(data) {
  const errors = [];
  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Esperado objeto de entities'] };
  }
  return { valid: errors.length === 0, errors };
}

export function validateEmptyArray(data) {
  if (!isArray(data)) return { valid: false, errors: ['Esperado array vazio'] };
  if (data.length !== 0) return { valid: false, errors: [`Esperado array vazio, mas tem ${data.length} itens`] };
  return { valid: true, errors: [] };
}

export function validateEmptyOrNormalizedPlaces(data) {
  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Esperado objeto normalizado'] };
  }
  const errors = [];
  if (!isArray(data.places)) errors.push('places: esperado array');
  if (!isArray(data.professionals)) errors.push('professionals: esperado array');
  if (!isArray(data.services)) errors.push('services: esperado array');
  if (!isArray(data.payments)) errors.push('payments: esperado array');
  return { valid: errors.length === 0, errors };
}

export function validateEmptyNormalizedPlaces(data) {
  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Esperado objeto normalizado vazio'] };
  }
  const errors = [];
  if (!isArray(data.places)) errors.push('places: esperado array');
  else if (data.places.length !== 0) errors.push(`places: esperado vazio, mas tem ${data.places.length} itens`);
  if (!isArray(data.professionals)) errors.push('professionals: esperado array');
  else if (data.professionals.length !== 0) errors.push(`professionals: esperado vazio, mas tem ${data.professionals.length} itens`);
  if (!isArray(data.services)) errors.push('services: esperado array');
  else if (data.services.length !== 0) errors.push(`services: esperado vazio, mas tem ${data.services.length} itens`);
  if (!isArray(data.payments)) errors.push('payments: esperado array');
  else if (data.payments.length !== 0) errors.push(`payments: esperado vazio, mas tem ${data.payments.length} itens`);
  return { valid: errors.length === 0, errors };
}

export function validateReportsSummary(data) {
  const errors = [];
  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Esperado objeto de relatório'] };
  }
  return { valid: errors.length === 0, errors };
}

export function validateUserTheme(data) {
  const errors = [];
  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Esperado objeto com theme'] };
  }
  return { valid: errors.length === 0, errors };
}

export function validatePageEntity(data) {
  const errors = validate(data, {
    id: isGuid,
    title: isString,
    slug: isString,
  });
  return { valid: errors.length === 0, errors };
}

export function validatePagesList(data) {
  if (!isArray(data)) return { valid: false, errors: ['Esperado array de pages'] };
  const errors = [];
  data.forEach((p, i) => {
    errors.push(...validate(p, {
      id: isGuid,
      title: isString,
    }, `[${i}]`));
  });
  return { valid: errors.length === 0, errors };
}

export function validateCascadeImpact(data) {
  const errors = [];
  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Esperado objeto DeleteImpactResponse'] };
  }
  if (!data.confirmHash && !data.hash) {
    errors.push('confirmHash ou hash: necessário para fase 2');
  }
  return { valid: errors.length === 0, errors };
}

export function validateNoContent() {
  return { valid: true, errors: [] };
}

export function validateArrayNotEmpty(data, entityName = 'items') {
  if (!isArray(data)) return { valid: false, errors: [`Esperado array de ${entityName}`] };
  if (data.length === 0) return { valid: false, errors: [`Array de ${entityName} está vazio, esperado pelo menos 1 item`] };
  return { valid: true, errors: [] };
}

export function validateSchedulesWithRelations(data) {
  const errors = [];
  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Esperado objeto { schedules, professionals, places }'] };
  }
  if (!isArray(data.schedules)) errors.push('schedules: esperado array');
  if (!isArray(data.professionals)) errors.push('professionals: esperado array');
  if (!isArray(data.places)) errors.push('places: esperado array');
  return { valid: errors.length === 0, errors };
}
